import { LoadingButton } from '@mui/lab'
import { Box, Button, Grid, InputLabel, Stack, TextField, Typography } from '@mui/material'
import { validatePartnerCode } from 'api/partnerCode'
import { ChangeEvent, KeyboardEvent, useState } from 'react'
import { ValidatePartnerCodeResponse } from 'types'
import { AuthFormTitle } from '../AuthFormTitle/AuthFormTitle'

interface Props {
  /** Called for every outcome that moves the flow on: funded (available or exhausted) or unfunded. */
  onValidated: (code: string, result: ValidatePartnerCodeResponse) => void
  onBack: () => void
  /** Registration with a funded code is in flight — keeps the button busy after validation. */
  submitting?: boolean
}

export function FamilyPartnerCodeForm({ onValidated, onBack, submitting = false }: Props) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [validating, setValidating] = useState(false)

  const trimmedCode = code.trim()
  const busy = validating || submitting

  const onChangeCode = (event: ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value)
    setError(null)
  }

  const handleContinue = async () => {
    if (!trimmedCode || busy) return

    setValidating(true)
    try {
      const { data } = await validatePartnerCode(trimmedCode)
      // Unknown and inactive codes keep the user here with the backend's explanation.
      if (data.outcome === 'invalid' || data.outcome === 'inactive') {
        setError(data.message)
        return
      }
      onValidated(trimmedCode, data)
    } catch {
      // The request failed outright; the global error handler has already shown why.
    } finally {
      setValidating(false)
    }
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleContinue()
    }
  }

  return (
    <Stack direction="column" justifyContent="space-between" spacing={2}>
      <Box>
        <AuthFormTitle textColor="secondary.main" mb={1}>
          Enter your Partner Code
        </AuthFormTitle>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Use the code you received from your partner organization.
        </Typography>

        <Stack spacing={1}>
          <InputLabel>Partner Code</InputLabel>
          <TextField
            required
            fullWidth
            name="partnerCode"
            value={code}
            onChange={onChangeCode}
            onKeyDown={onKeyDown}
            autoFocus
            autoComplete="off"
            size="small"
            error={!!error}
            helperText={error}
            // Codes are uppercase; showing them that way saves the user from guessing the case.
            inputProps={{ style: { textTransform: 'uppercase' } }}
          />
        </Stack>
      </Box>

      {/* Secondary action first and outlined, as on the other registration forms. */}
      <Grid container spacing={2} direction="row" alignItems="center" justifyContent="center">
        <Grid item>
          <Button
            onClick={onBack}
            disabled={busy}
            variant="outlined"
            color="info"
            sx={{ minWidth: 200 }}
          >
            Back
          </Button>
        </Grid>
        <Grid item>
          <LoadingButton
            onClick={handleContinue}
            loading={busy}
            disabled={!trimmedCode}
            variant="contained"
            color="secondary"
            sx={{ minWidth: 200 }}
          >
            Continue
          </LoadingButton>
        </Grid>
      </Grid>
    </Stack>
  )
}
